import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import './SessionTimeout.css';

/** Parse JWT expiry from token (returns epoch in ms) */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Format remaining time as "Xm Xs" */
function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `${min}m ${sec.toString().padStart(2, '0')}s`;
  return `${sec}s`;
}

const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // Show warning 5 min before expiry

function SessionTimeout() {
  const { t } = useTranslation();
  const { token, logout } = useAuth();
  const [remaining, setRemaining] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkExpiry = useCallback(() => {
    if (!token) return;
    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    const diff = expiry - Date.now();
    setRemaining(diff);

    if (diff <= 0) {
      logout();
      return;
    }

    if (diff <= WARNING_THRESHOLD_MS && !dismissed) {
      setShowWarning(true);
    }
  }, [token, dismissed, logout]);

  useEffect(() => {
    checkExpiry();
    intervalRef.current = setInterval(checkExpiry, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkExpiry]);

  // Reset dismissed when token changes (re-login)
  useEffect(() => {
    setDismissed(false);
    setShowWarning(false);
  }, [token]);

  const handleDismiss = () => {
    setDismissed(true);
    setShowWarning(false);
  };

  const handleLogout = () => {
    setShowWarning(false);
    logout();
  };

  if (!showWarning || remaining === null || remaining <= 0) return null;

  return (
    <div className="session-timeout-overlay">
      <div className="session-timeout">
        <div className="session-timeout__icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>

        <div className="session-timeout__content">
          <h3 className="session-timeout__title">{t('components.sessionTimeout.title')}</h3>
          <p className="session-timeout__text"
            dangerouslySetInnerHTML={{ __html: t('components.sessionTimeout.message', { time: formatRemaining(remaining) }) }}
          />
        </div>

        <div className="session-timeout__actions">
          <button className="btn btn-sm btn-outline" onClick={handleDismiss}>
            {t('components.sessionTimeout.dismiss')}
          </button>
          <button className="btn btn-sm btn-danger" onClick={handleLogout}>
            {t('components.sessionTimeout.logoutNow')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTimeout;
